import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Button } from '@/components/ui/button';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import './SharedFormButtons.css';

type SharedFormButtonsProps = {
  onCancel: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
  isSubmitting?: boolean;
  isUpdating?: boolean;
  submitDisabled?: boolean;
  submitDisabledTooltip?: string;
  showGroupWarning?: boolean;
};

export const SharedFormButtons: React.FC<
  React.PropsWithChildren<SharedFormButtonsProps>
> = ({
  onCancel,
  onSubmit,
  isLoading = false,
  isSubmitting = false,
  isUpdating = false,
  submitDisabled = false,
  submitDisabledTooltip,
  showGroupWarning = false,
}) => {
  const { t } = useTranslation();

  const submitButton = (
    <Button
      variant="primary"
      type="submit"
      onClick={onSubmit}
      disabled={submitDisabled || isSubmitting || isLoading}>
      {isSubmitting ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"></span>
          {t('expenses.createEditModal.submitting')}
        </>
      ) : (
        t('expenses.createEditModal.submit')
      )}
    </Button>
  );

  return (
    <div
      className={`d-flex ${
        isUpdating ? 'justify-content-end' : 'justify-content-between'
      } align-items-center gap-2 flex-wrap mt-3`}>
      <Alert
        variant="light"
        className={`mb-0 py-2 px-3 d-flex align-items-center draft-info-alert ${
          !isUpdating ? 'draft-info-visible' : 'draft-info-hidden'
        }`}>
        <FlairIcon
          icon="information-circle-outline"
          size="sm"
          className="me-2 flex-shrink-0"
        />
        <small className="mb-0">
          {t('expenses.createEditModal.draftInfo')}
        </small>
      </Alert>
      <div className="d-flex gap-2">
        {isUpdating && (
          <div className="d-flex align-items-center me-2">
            <small className="text-primary">
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"></span>
              {t('expenses.createEditModal.updating')}
            </small>
          </div>
        )}
        <Button
          variant="outline-secondary"
          type="button"
          onClick={onCancel}
          disabled={isLoading}>
          {t('expenses.createEditModal.close')}
        </Button>
        {showGroupWarning && submitDisabledTooltip ? (
          <OverlayTrigger
            overlay={
              <Tooltip id="submit-disabled-tooltip">
                {submitDisabledTooltip}
              </Tooltip>
            }
            placement="top">
            <span>{submitButton}</span>
          </OverlayTrigger>
        ) : (
          submitButton
        )}
      </div>
    </div>
  );
};

type FinalAmountDisplayProps = {
  finalAmount?: number;
  currencyCode?: string;
  formatCurrency: (params: { value: number; currency: string }) => string;
};

export const FinalAmountDisplay: React.FC<
  React.PropsWithChildren<FinalAmountDisplayProps>
> = ({ finalAmount, currencyCode, formatCurrency }) => {
  const { t } = useTranslation();

  return (
    <div className="d-flex justify-content-between align-items-center mb-5">
      <div className="fs-4 fw-bold">
        {t('expenses.fields.finalAmountLabel')}
      </div>
      <div className={`text-end fs-3 fw-bold text-success`}>
        {finalAmount !== undefined && currencyCode
          ? formatCurrency({ value: finalAmount, currency: currencyCode })
          : '?'}
      </div>
    </div>
  );
};
